import { CommonModule } from '@angular/common';
import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';

import { isEmpty } from '@app/util/util';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import {
  RadioButtonAlign, RadioButtonFailedType, RadioButtonSize, RadioButtonText
} from '@design-system/components/organisms/radio-button/entities/radio-button.types';
import {
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';
import { ImageSize } from '@design-system/components/molecules/image/entities/image.types';
import { IconDsComponent } from '@design-system/components/atoms/icon/icon.component';
import { TypographyDsComponent } from '@design-system/components/atoms/typography/typography.component';
import { ImageDsComponent } from '@design-system/components/molecules/image/image.component';
import { DataDsComponent } from '@design-system/components/molecules/data/data.component';

@Component({
  selector: 'app-ds-radio-button',
  templateUrl: './radio-button.component.html',
  styleUrls: ['./radio-button.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    IconDsComponent,
    ImageDsComponent,
    TypographyDsComponent,
    DataDsComponent,
  ]
})
export class RadioButtonDsComponent {

  @Input() size = RadioButtonSize.SM;
  @Input() align = RadioButtonAlign.Left;
  @Input() failedType = RadioButtonFailedType.Error;
  @Input() isFailed = false;
  @Input() isDisabled = false;
  @Input() checked = false;
  @Input() title: RadioButtonText;
  @Input() subtitles: RadioButtonText[] = [];
  @Input() icon: Icon;
  @Input() image: string;
  @Input() counter = 0;

  @Output() radioOnChecked: EventEmitter<void> = new EventEmitter<void>();

  public readonly imageSize = ImageSize.MD;

  constructor() { }

  get failedClass(): string {
    return this.isFailed ? this.failedType : RadioButtonFailedType.None;
  }

  get isTitlesEmpty(): boolean {
    return isEmpty(this.title) && isEmpty(this.subtitles);
  }

  get alignIsLeft(): boolean {
    return this.align === RadioButtonAlign.Left;
  }

  get alignIsRight(): boolean {
    return this.align === RadioButtonAlign.Right;
  }

  get iconFillColor(): IconFillColor {
    return this.isDisabled ? IconFillColor.Disabled : IconFillColor.Primary;
  }

  public fontColor(label: RadioButtonText, isSubtitle: boolean): TypographyColor {
    return this.isDisabled ? TypographyColor.Disabled :
      label?.CustomColor ? label.CustomColor : (isSubtitle ? TypographyColor.Secondary : TypographyColor.Primary);
  }

  public fontSize(label: RadioButtonText | undefined = undefined): TypographyFontSize {
    return this.size === RadioButtonSize.CustomSize && label?.CustomSize ? label.CustomSize :
      this.size === RadioButtonSize.SM ? TypographyFontSize.XXS : TypographyFontSize.XS;
  }

  public fontFamily(label: RadioButtonText): TypographyFontFamily {
    return label.Bold ? TypographyFontFamily.SemiBold : TypographyFontFamily.Regular;
  }

  public iconColor(label: RadioButtonText, isSubtitle: boolean): IconFillColor {
    return this.isDisabled ? IconFillColor.Disabled : label?.CustomIconFill ? label.CustomIconFill :
      (isSubtitle ? IconFillColor.Secondary : IconFillColor.Primary);
  }

  public checkEvent(): void {
    if (!this.checked) {
      this.radioOnChecked.emit();
    }
  }

}
